/**
 * AdminVideos.jsx
 * Admin dashboard page for managing Video Testimonials.
 *
 * Features:
 *  - Upload a video FILE to MongoDB GridFS (up to 50MB), OR
 *  - Add an external URL (YouTube, Vimeo, direct .mp4 link)
 *  - "Show on pages" multi-checkbox: homepage + all course slugs from DB
 *  - Publish / unpublish toggle per video
 *  - Edit name, URL, pages, order
 *  - Delete (removes GridFS file if applicable)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../homepage/navbar/Navbar';
import { useThemeContext } from '../../context/theme-context';
import './admin.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

// ── Pages multi-select dropdown ────────────────────────────────────────────
/**
 * PageSelector — a checkbox dropdown to pick which pages a review/video shows on.
 * @param {string[]} selected  — currently selected page slugs
 * @param {Function} onChange  — called with new array of slugs
 * @param {Array}    pages     — [{ value, label }] list of available pages
 */
const PageSelector = ({ selected = [], onChange, pages = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  };

  const label = selected.length === 0
    ? 'All pages (no filter)'
    : selected.length === 1
      ? pages.find(p => p.value === selected[0])?.label || selected[0]
      : `${selected.length} pages selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '0.5rem 0.8rem',
          borderRadius: '6px',
          border: '1px solid rgba(0,0,0,0.15)',
          background: 'var(--color-white)',
          fontSize: '0.85rem',
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
          color: 'var(--color-dark)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 100,
          background: 'var(--color-white)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: '220px',
          maxHeight: '260px',
          overflowY: 'auto',
          padding: '0.5rem',
        }}>
          <div style={{ fontSize: '0.75rem', opacity: 0.5, padding: '0.25rem 0.5rem', marginBottom: '0.25rem' }}>
            Empty = show on all pages
          </div>
          {pages.map(p => (
            <label
              key={p.value}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.35rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.85rem', color: 'var(--color-dark)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <input
                type="checkbox"
                checked={selected.includes(p.value)}
                onChange={() => toggle(p.value)}
                style={{ cursor: 'pointer' }}
              />
              {p.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Empty form factory ─────────────────────────────────────────────────────
const emptyForm = () => ({
  name: '',
  videoUrl: '',
  pages: [],
  order: 0,
  published: true,
  uploadMode: false,  // false = URL entry, true = file upload
});

// ── Main Component ─────────────────────────────────────────────────────────
const AdminVideos = () => {
  const { themeState } = useThemeContext();
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [availablePages, setAvailablePages] = useState([
    { value: 'homepage', label: '🏠 Homepage' },
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // 'uploading' | null
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: token ? `Bearer ${token}` : '' };

  // ── Load available page options (homepage + course slugs) ──────────────
  const fetchPageOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/all`, {
        headers: authHeader,
        credentials: 'include'
      });
      const json = await res.json();
      if (res.ok && json.success && json.data) {
        const courseOptions = json.data.map(c => ({
          value: c.slug,
          label: `📚 ${c.shortTitle || c.title}`,
        }));
        setAvailablePages([{ value: 'homepage', label: '🏠 Homepage' }, ...courseOptions]);
      }
    } catch { /* Keep defaults */ }
  };

  // ── Fetch all videos ───────────────────────────────────────────────────
  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/videos/admin/all`, {
        headers: authHeader,
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) setVideos(json.data);
    } catch {
      setError('Failed to connect to backend.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
    fetchPageOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form helpers ───────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSelectedFile(null);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const openEdit = (video) => {
    setEditingId(video._id);
    setForm({
      name: video.name || '',
      videoUrl: video.videoUrl || '',
      pages: video.pages || [],
      order: video.order ?? 0,
      published: video.published ?? true,
      uploadMode: false,
    });
    setSelectedFile(null);
    setShowForm(true);
    setError(null);
    setSuccess(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    setSelectedFile(null);
    setError(null);
  };

  // ── Save: URL-only entry or edit ───────────────────────────────────────
  const handleSaveUrl = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!editingId && !form.videoUrl.trim()) { setError('Video URL is required for URL-only entries.'); return; }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        name: form.name,
        videoUrl: form.videoUrl,
        pages: form.pages,
        order: form.order,
        published: form.published,
      };
      const url = editingId ? `${API_URL}/api/videos/${editingId}` : `${API_URL}/api/videos`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) {
        setSuccess(editingId ? 'Video updated!' : 'Video added!');
        cancelForm();
        fetchVideos();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(json.message || 'Failed to save.');
      }
    } catch { setError('Connection error.'); }
    finally { setSaving(false); }
  };

  // ── Save: file upload ──────────────────────────────────────────────────
  const handleUploadFile = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!selectedFile) { setError('Please select a video file.'); return; }
    try {
      setUploadProgress('uploading');
      setError(null);
      const fd = new FormData();
      fd.append('video', selectedFile);
      fd.append('name', form.name);
      fd.append('pages', JSON.stringify(form.pages));
      fd.append('order', form.order);
      const res = await fetch(`${API_URL}/api/videos/upload`, {
        method: 'POST',
        headers: authHeader,
        credentials: 'include',
        body: fd,
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) {
        setSuccess('Video uploaded and saved!');
        cancelForm();
        fetchVideos();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(json.message || 'Failed to upload.');
      }
    } catch { setError('Connection error.'); }
    finally { setUploadProgress(null); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete video for "${name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/videos/${id}`, {
        method: 'DELETE',
        headers: authHeader,
        credentials: 'include'
      });
      if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
      const json = await res.json();
      if (res.ok && json.success) {
        setSuccess('Video deleted.');
        fetchVideos();
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch { setError('Failed to delete.'); }
  };

  const handleTogglePublish = async (video) => {
    try {
      const res = await fetch(`${API_URL}/api/videos/${video._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        credentials: 'include',
        body: JSON.stringify({ ...video, published: !video.published }),
      });
      if (res.ok) fetchVideos();
    } catch { setError('Failed to update publish status.'); }
  };

  // ── Shared styles ──────────────────────────────────────────────────────
  const inputStyle = {
    padding: '0.6rem 0.8rem', borderRadius: '6px',
    border: '1px solid rgba(0,0,0,0.12)',
    background: 'var(--color-white)', fontFamily: 'inherit',
    fontSize: '0.9rem', width: '100%', boxSizing: 'border-box',
    color: 'var(--color-dark)',
  };
  const labelStyle = { fontWeight: 600, fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' };

  const isUploading = uploadProgress === 'uploading';

  return (
    <main className={`${themeState.primary} ${themeState.background} admin__page`}>
      <Navbar />

      <section className="admin__container">
        <br />

        {/* ── Page header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button className="btn sm" onClick={() => navigate('/admin')}>← Back</button>
            <h2 style={{ margin: 0 }}>Video Testimonials</h2>
          </div>
          <button className="btn primary" onClick={openAdd}>+ Add Video</button>
        </div>

        {/* ── Status messages ── */}
        {error && <div className="admin__error">{error}</div>}
        {success && <div className="admin__error" style={{ background: 'rgba(76,175,80,0.12)', color: '#2e7d32' }}>{success}</div>}

        {/* ── Add / Edit Form ── */}
        {showForm && (
          <div style={{
            background: 'var(--color-white)', border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          }}>
            <h3 style={{ margin: '0 0 1.25rem', fontSize: '1.1rem' }}>
              {editingId ? 'Edit Video Entry' : 'Add New Video'}
            </h3>

            {/* Toggle upload mode (only on add, not edit) */}
            {!editingId && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <button
                  type="button"
                  className={`btn sm ${!form.uploadMode ? 'primary' : ''}`}
                  onClick={() => setForm(p => ({ ...p, uploadMode: false }))}
                >
                  🔗 Paste URL
                </button>
                <button
                  type="button"
                  className={`btn sm ${form.uploadMode ? 'primary' : ''}`}
                  onClick={() => setForm(p => ({ ...p, uploadMode: true }))}
                >
                  📁 Upload File
                </button>
              </div>
            )}

            <form onSubmit={form.uploadMode ? handleUploadFile : handleSaveUrl}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>

                {/* Name */}
                <div>
                  <label style={labelStyle}>Person's Name *</label>
                  <input
                    style={inputStyle}
                    placeholder="e.g. Joshua Olatunbosun"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  />
                </div>

                {/* URL or file input */}
                {form.uploadMode ? (
                  <div>
                    <label style={labelStyle}>Video File * (mp4, webm — max 50MB)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/ogg,video/quicktime"
                      onChange={e => setSelectedFile(e.target.files[0] || null)}
                      style={{ ...inputStyle, padding: '0.45rem' }}
                    />
                    {selectedFile && (
                      <div style={{ fontSize: '0.8rem', marginTop: '0.3rem', color: '#2e7d32' }}>
                        ✅ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label style={labelStyle}>Video URL *</label>
                    <input
                      style={inputStyle}
                      placeholder="https://example.com/video.mp4 or YouTube link"
                      value={form.videoUrl}
                      onChange={e => setForm(p => ({ ...p, videoUrl: e.target.value }))}
                    />
                    <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.2rem' }}>
                      Supports: direct .mp4 links, YouTube, Vimeo, Google Drive (public)
                    </div>
                  </div>
                )}

                {/* Show on pages */}
                <div>
                  <label style={labelStyle}>Show on Pages</label>
                  <PageSelector
                    selected={form.pages}
                    onChange={pages => setForm(p => ({ ...p, pages }))}
                    pages={availablePages}
                  />
                  <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.2rem' }}>
                    Leave empty = show on all pages
                  </div>
                </div>

                {/* Order + published row */}
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem', alignItems: 'center' }}>
                  <div>
                    <label style={labelStyle}>Display Order</label>
                    <input
                      type="number"
                      style={{ ...inputStyle, width: '100%' }}
                      value={form.order}
                      min={0}
                      onChange={e => setForm(p => ({ ...p, order: Number(e.target.value) }))}
                    />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', paddingTop: '1.2rem' }}>
                    <input
                      type="checkbox"
                      checked={form.published}
                      onChange={e => setForm(p => ({ ...p, published: e.target.checked }))}
                      style={{ width: '1rem', height: '1rem' }}
                    />
                    Published (visible on site)
                  </label>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                  <button type="submit" className="btn primary" disabled={saving || isUploading}>
                    {isUploading ? '⏳ Uploading...' : saving ? 'Saving...' : editingId ? 'Update Video' : form.uploadMode ? '⬆ Upload & Save' : 'Add Video'}
                  </button>
                  <button type="button" className="btn" onClick={cancelForm}>Cancel</button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* ── Video list ── */}
        {loading ? (
          <div className="admin__loading">Loading videos...</div>
        ) : videos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', opacity: 0.5 }}>
            <p>No video testimonials yet.</p>
            <p style={{ fontSize: '0.85rem' }}>Add a video above to display it on the site.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {videos.map(video => {
              const src = video.resolvedUrl;
              return (
                <div
                  key={video._id}
                  style={{
                    background: 'var(--color-white)', border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: '10px', padding: '1rem 1.25rem',
                    display: 'grid', gridTemplateColumns: '64px 1fr auto',
                    gap: '1rem', alignItems: 'center',
                    opacity: video.published ? 1 : 0.55,
                    transition: 'opacity 0.2s',
                  }}
                >
                  {/* Thumbnail */}
                  <div style={{
                    width: '64px', height: '48px', borderRadius: '6px',
                    background: '#1a1a2e', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
                  }}>
                    {src ? (
                      <video
                        src={src}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        muted
                        preload="metadata"
                      />
                    ) : (
                      <span style={{ fontSize: '1.5rem' }}>🎬</span>
                    )}
                  </div>

                  {/* Info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{video.name}</span>
                      {video.videoFileId && (
                        <span style={{ fontSize: '0.7rem', background: 'rgba(66,133,244,0.1)', color: '#4285F4', border: '1px solid rgba(66,133,244,0.2)', borderRadius: '4px', padding: '0.1rem 0.4rem' }}>
                          GridFS Upload
                        </span>
                      )}
                      {!video.published && (
                        <span style={{ fontSize: '0.7rem', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', padding: '0.1rem 0.4rem', color: '#777' }}>
                          Unpublished
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.78rem', opacity: 0.6, marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {src || '(no URL)'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-dark)', opacity: 0.6 }}>Show on:</span>
                      <div style={{ width: '180px' }}>
                        <PageSelector
                          selected={video.pages || []}
                          onChange={async (newPages) => {
                            setVideos(prev => prev.map(v => v._id === video._id ? { ...v, pages: newPages } : v));
                            try {
                              const token = localStorage.getItem('token');
                              await fetch(`${API_URL}/api/videos/${video._id}`, {
                                method: 'PUT',
                                credentials: 'include',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: token ? `Bearer ${token}` : '',
                                },
                                body: JSON.stringify({ ...video, pages: newPages }),
                              });
                            } catch (err) {
                              console.error("Failed to update video pages inline:", err);
                            }
                          }}
                          pages={availablePages}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button className="btn sm" onClick={() => handleTogglePublish(video)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                      {video.published ? '👁 Hide' : '👁 Show'}
                    </button>
                    <button className="btn sm primary" onClick={() => openEdit(video)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                      Edit
                    </button>
                    <button className="btn sm" onClick={() => handleDelete(video._id, video.name)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', background: 'rgba(211,47,47,0.1)', color: '#c62828' }}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <br /><br />
      </section>
    </main>
  );
};

export default AdminVideos;
